import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col, Typography, Tag, Progress, Tooltip } from 'antd';
import {
  CustomerServiceOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { DataCard, StatCard } from '../common';
import { useTenant } from '../../context/TenantContext';
import {
  useAutotaskIdFromCipp,
  useAutotaskTickets,
  useAutotaskStats,
  useAutotaskSLA,
} from '../../hooks';

const { Text } = Typography;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricValue = styled(Text)`
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const PriorityBreakdown = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

const HealthIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HealthDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $status }) => {
    switch ($status) {
      case 'healthy': return 'var(--odin-success)';
      case 'warning': return 'var(--odin-warning)';
      case 'critical': return 'var(--odin-error)';
      default: return 'var(--odin-text-tertiary)';
    }
  }};
`;

const MappingWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing.sm};
  background: var(--odin-status-awaiting-bg);
  border: 1px solid var(--odin-status-awaiting-border);
  border-radius: 4px;
  color: var(--odin-status-awaiting-text);
  font-size: 12px;
  margin-bottom: 12px;
`;

/**
 * ServiceDeskWidget - Displays Autotask service desk data
 *
 * This component integrates with Autotask-MCP to display:
 * - Open tickets count
 * - Resolved today count
 * - Average response time
 * - SLA compliance percentage
 * - Priority breakdown (Critical, High, Medium, Low)
 *
 * Uses customerMappingService (via useAutotaskIdFromCipp) to resolve
 * CIPP tenant IDs to Autotask company IDs (integers).
 *
 * @param {Object} props
 * @param {number} props.companyId - Optional direct Autotask company ID (integer, overrides mapping)
 * @param {Object} props.data - Legacy data prop for backward compatibility
 * @param {boolean} props.loading - Loading state (legacy)
 * @param {Object} props.error - Error object (legacy)
 * @param {Function} props.onRefresh - Refresh callback
 * @param {boolean} props.compact - Show compact StatCard view
 */
const ServiceDeskWidget = ({
  companyId: propCompanyId = null,
  data: legacyData = null,
  loading: legacyLoading = false,
  error: legacyError = null,
  onRefresh,
  compact = false,
}) => {
  // Get selected tenant from context (CIPP tenant ID)
  const { selectedTenant } = useTenant();
  const cippTenantId = selectedTenant?.id;

  // Resolve CIPP tenant ID to Autotask company ID using customer mapping service
  const {
    autotaskId: mappedAutotaskId,
    isResolved,
    isLoading: isMappingLoading,
    customer,
  } = useAutotaskIdFromCipp(cippTenantId, {
    enabled: !!cippTenantId && propCompanyId === null,
  });

  // Use prop companyId if provided, otherwise use mapped Autotask ID
  const autotaskId = propCompanyId ?? mappedAutotaskId;
  const hasAutotaskMapping = propCompanyId !== null || isResolved;

  // Fetch Autotask tickets data
  const {
    data: ticketsData,
    isLoading: ticketsLoading,
    error: ticketsError,
    refetch: refetchTickets,
  } = useAutotaskTickets(autotaskId, {
    enabled: autotaskId !== null,
  });

  // Fetch Autotask stats data
  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useAutotaskStats(autotaskId, {
    enabled: autotaskId !== null,
  });

  // Fetch Autotask SLA performance data
  const {
    data: slaData,
    isLoading: slaLoading,
    error: slaError,
    refetch: refetchSLA,
  } = useAutotaskSLA(autotaskId, {
    enabled: autotaskId !== null,
  });

  // Calculate metrics from Autotask data
  const metrics = useMemo(() => {
    // If legacy data is provided, use it for backward compatibility
    if (legacyData && Object.keys(legacyData).length > 0) {
      return {
        openTickets: legacyData.openTickets ?? null,
        resolvedToday: legacyData.resolvedToday ?? null,
        avgResponseTime: legacyData.avgResponseTime ?? null,
        slaCompliance: legacyData.slaCompliance ?? null,
        criticalTickets: legacyData.criticalTickets ?? null,
        highTickets: legacyData.highTickets ?? null,
        mediumTickets: legacyData.mediumTickets ?? null,
        lowTickets: legacyData.lowTickets ?? null,
        healthStatus: null,
      };
    }

    // Autotask status codes (numeric)
    const CLOSED_STATUS_CODES = [5, 8]; // 5=Complete, 8=Closed

    // Parse tickets from Autotask-MCP response
    let parsedTickets = [];
    if (ticketsData) {
      // Handle MCP response structure (may be wrapped in content array or direct JSON)
      if (typeof ticketsData === 'string') {
        try {
          const parsed = JSON.parse(ticketsData);
          // Autotask returns { success, count, data: [...tickets...] }
          parsedTickets = Array.isArray(parsed.data) ? parsed.data : (parsed.data?.tickets || parsed.tickets || parsed || []);
        } catch {
          parsedTickets = [];
        }
      } else if (Array.isArray(ticketsData.data)) {
        // Direct response: { success, count, data: [...tickets...] }
        parsedTickets = ticketsData.data;
      } else if (ticketsData.data?.tickets) {
        parsedTickets = ticketsData.data.tickets;
      } else if (ticketsData.tickets) {
        parsedTickets = ticketsData.tickets;
      } else if (Array.isArray(ticketsData)) {
        parsedTickets = ticketsData;
      } else if (ticketsData.content?.[0]?.text) {
        try {
          const parsed = JSON.parse(ticketsData.content[0].text);
          parsedTickets = Array.isArray(parsed.data) ? parsed.data : (parsed.data?.tickets || parsed.tickets || parsed || []);
        } catch {
          parsedTickets = [];
        }
      }
    }

    // Ensure parsedTickets is an array
    if (!Array.isArray(parsedTickets)) {
      parsedTickets = [];
    }

    // Parse stats from Autotask-MCP response
    let parsedStats = null;
    if (statsData) {
      if (typeof statsData === 'string') {
        try {
          parsedStats = JSON.parse(statsData);
        } catch {
          parsedStats = null;
        }
      } else if (statsData.data) {
        parsedStats = statsData.data;
      } else if (statsData.content?.[0]?.text) {
        try {
          parsedStats = JSON.parse(statsData.content[0].text);
        } catch {
          parsedStats = null;
        }
      } else {
        parsedStats = statsData;
      }
    }

    // Parse SLA data from Autotask-MCP response
    let parsedSLA = null;
    if (slaData) {
      if (typeof slaData === 'string') {
        try {
          parsedSLA = JSON.parse(slaData);
        } catch {
          parsedSLA = null;
        }
      } else if (slaData.data) {
        parsedSLA = slaData.data;
      } else if (slaData.content?.[0]?.text) {
        try {
          parsedSLA = JSON.parse(slaData.content[0].text);
        } catch {
          parsedSLA = null;
        }
      } else {
        parsedSLA = slaData;
      }
    }

    // Calculate open tickets (not Complete or Closed)
    // Autotask uses numeric status codes: 5=Complete, 8=Closed
    const openTickets = parsedTickets.filter(
      t => !CLOSED_STATUS_CODES.includes(t.status)
    ).length;

    // Calculate resolved today (tickets closed/completed today)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const resolvedToday = parsedTickets.filter(t => {
      // Check for completed status using numeric codes
      if (!CLOSED_STATUS_CODES.includes(t.status)) return false;
      const completedDate = t.completedDate || t.lastActivityDate;
      if (!completedDate) return false;
      const ticketDate = new Date(completedDate);
      ticketDate.setHours(0, 0, 0, 0);
      return ticketDate.getTime() === today.getTime();
    }).length;

    // Get average response time from stats or calculate
    const avgResponseTime = parsedStats?.avgResponseTime ||
                            parsedStats?.averageResponseTime ||
                            parsedStats?.stats?.avgResponseTime ||
                            null;

    // Get SLA compliance from SLA data or stats
    const slaCompliance = parsedSLA?.slaCompliance ||
                          parsedSLA?.compliance ||
                          parsedSLA?.overallCompliance ||
                          parsedStats?.slaCompliance ||
                          null;

    // Calculate priority breakdown (Autotask uses numeric: 1=Low, 2=Medium, 3=High, 4=Critical)
    const criticalTickets = parsedTickets.filter(
      t => t.priority === 4
    ).length;
    const highTickets = parsedTickets.filter(
      t => t.priority === 3
    ).length;
    const mediumTickets = parsedTickets.filter(
      t => t.priority === 2
    ).length;
    const lowTickets = parsedTickets.filter(
      t => t.priority === 1
    ).length;

    // Determine health status based on SLA and critical tickets
    let healthStatus = 'healthy';
    if (criticalTickets > 0 || (slaCompliance !== null && slaCompliance < 80)) {
      healthStatus = 'critical';
    } else if (highTickets > 3 || (slaCompliance !== null && slaCompliance < 95)) {
      healthStatus = 'warning';
    }

    return {
      openTickets: ticketsData !== undefined ? openTickets : null,
      resolvedToday: resolvedToday || 0,
      avgResponseTime,
      slaCompliance,
      criticalTickets: criticalTickets || 0,
      highTickets: highTickets || 0,
      mediumTickets: mediumTickets || 0,
      lowTickets: lowTickets || 0,
      healthStatus,
    };
  }, [legacyData, ticketsData, statsData, slaData]);

  // Determine loading state
  const isLoading = legacyLoading || isMappingLoading || ticketsLoading || statsLoading || slaLoading;

  // Determine error state
  const error = legacyError || ticketsError || statsError || slaError;

  // Refresh handler
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
    if (autotaskId !== null) {
      refetchTickets();
      refetchStats();
      refetchSLA();
    }
  }, [onRefresh, autotaskId, refetchTickets, refetchStats, refetchSLA]);

  // Show mapping status warning if no Autotask mapping found
  const showMappingWarning = cippTenantId && !hasAutotaskMapping && !isMappingLoading && propCompanyId === null;

  // Compact view (StatCard)
  if (compact) {
    return (
      <StatCard
        icon={CustomerServiceOutlined}
        value={metrics.openTickets}
        label="Open Tickets"
        loading={isLoading}
        tooltip={
          hasAutotaskMapping
            ? `Open tickets in Autotask${customer ? ` (${customer.displayName})` : ''}`
            : 'Configure Autotask mapping in Odin-MCP to view data'
        }
      />
    );
  }

  // Full widget view
  return (
    <DataCard
      title="Autotask"
      icon={CustomerServiceOutlined}
      loading={isLoading}
      error={error}
      onRefresh={handleRefresh}
      empty={metrics.openTickets === null && !showMappingWarning}
      extra={
        metrics.healthStatus && metrics.openTickets !== null && (
          <Tooltip title={`Service desk health: ${metrics.healthStatus}`}>
            <HealthIndicator>
              <HealthDot $status={metrics.healthStatus} />
              <Text style={{ fontSize: 12, color: 'var(--odin-text-secondary)' }}>
                {metrics.healthStatus === 'healthy' ? 'Healthy' :
                 metrics.healthStatus === 'warning' ? 'Warning' : 'Critical'}
              </Text>
            </HealthIndicator>
          </Tooltip>
        )
      }
    >
      {showMappingWarning && (
        <MappingWarning>
          <LinkOutlined />
          <span>
            No Autotask mapping found for this tenant. Add mapping in Odin-MCP config.
          </span>
        </MappingWarning>
      )}

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <FileTextOutlined />
              Open Tickets
            </MetricLabel>
            <MetricValue>{metrics.openTickets ?? '--'}</MetricValue>
          </MetricRow>
        </Col>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <CheckCircleOutlined />
              Resolved Today
            </MetricLabel>
            <MetricValue>{metrics.resolvedToday ?? '--'}</MetricValue>
          </MetricRow>
        </Col>
        <Col span={24}>
          <MetricRow>
            <MetricLabel>
              <ClockCircleOutlined />
              Avg Response Time
            </MetricLabel>
            <MetricValue>
              {metrics.avgResponseTime !== null ? `${metrics.avgResponseTime} min` : '--'}
            </MetricValue>
          </MetricRow>
        </Col>
        <Col span={24}>
          <MetricRow>
            <MetricLabel>
              <CheckCircleOutlined />
              SLA Compliance
            </MetricLabel>
            <MetricValue>
              {metrics.slaCompliance !== null ? (
                <Progress
                  percent={metrics.slaCompliance}
                  size="small"
                  style={{ width: 100 }}
                  strokeColor={metrics.slaCompliance >= 95 ? 'var(--odin-success)' : metrics.slaCompliance >= 80 ? 'var(--odin-warning)' : 'var(--odin-error)'}
                />
              ) : '--'}
            </MetricValue>
          </MetricRow>
        </Col>
        {(metrics.criticalTickets > 0 || metrics.highTickets > 0 || metrics.mediumTickets > 0 || metrics.lowTickets > 0) && (
          <Col span={24}>
            <MetricLabel style={{ marginBottom: 8 }}>
              <ExclamationCircleOutlined />
              Priority Breakdown
            </MetricLabel>
            <PriorityBreakdown>
              {metrics.criticalTickets > 0 && (
                <Tag color="red">Critical: {metrics.criticalTickets}</Tag>
              )}
              {metrics.highTickets > 0 && (
                <Tag color="orange">High: {metrics.highTickets}</Tag>
              )}
              {metrics.mediumTickets > 0 && (
                <Tag color="default">Medium: {metrics.mediumTickets}</Tag>
              )}
              {metrics.lowTickets > 0 && (
                <Tag color="green">Low: {metrics.lowTickets}</Tag>
              )}
            </PriorityBreakdown>
          </Col>
        )}
      </Row>
    </DataCard>
  );
};

ServiceDeskWidget.propTypes = {
  /** Direct Autotask company ID (integer, bypasses customer mapping) */
  companyId: PropTypes.number,
  /** Legacy data prop for backward compatibility */
  data: PropTypes.shape({
    openTickets: PropTypes.number,
    resolvedToday: PropTypes.number,
    avgResponseTime: PropTypes.number,
    slaCompliance: PropTypes.number,
    criticalTickets: PropTypes.number,
    highTickets: PropTypes.number,
    mediumTickets: PropTypes.number,
    lowTickets: PropTypes.number,
  }),
  /** Legacy loading state */
  loading: PropTypes.bool,
  /** Legacy error object */
  error: PropTypes.object,
  /** Callback when refresh is requested */
  onRefresh: PropTypes.func,
  /** Show compact StatCard view */
  compact: PropTypes.bool,
};

export default ServiceDeskWidget;
